<template>
  <div class="faq-page">
    <div class="app-page-top-spacer">
      <h1 class="faq-title">{{ $t("faq.title") }}</h1>
      <p class="faq-subtitle">{{ $t("faq.subtitle") }}</p>
    </div>

    <div class="faq-container">
      <!-- Pattern Related Questions -->
      <div class="faq-section">
        <h2 class="faq-section-title">{{ $t("faq.pattern_related") }}</h2>
        <div class="faq-item" v-for="(item, index) in patternQuestions" :key="`pattern-${index}`">
          <div class="faq-question" @click="togglePatternItem(index)">
            <h3 class="question-text">{{ $t(item.questionKey) }}</h3>
            <v-icon
              :icon="item.isOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"
              class="expand-icon"
            />
          </div>
          <div class="faq-answer" :class="{ 'is-open': item.isOpen }" v-show="item.isOpen">
            <p>{{ $t(item.answerKey) }}</p>
          </div>
        </div>
      </div>

      <!-- General Questions -->
      <div class="faq-section">
        <h2 class="faq-section-title">{{ $t("faq.general") }}</h2>
        <div class="faq-item" v-for="(item, index) in generalQuestions" :key="`general-${index}`">
          <div class="faq-question" @click="toggleGeneralItem(index)">
            <h3 class="question-text">{{ $t(item.questionKey) }}</h3>
            <v-icon
              :icon="item.isOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"
              class="expand-icon"
            />
          </div>
          <div class="faq-answer" :class="{ 'is-open': item.isOpen }" v-show="item.isOpen">
            <p>{{ $t(item.answerKey) }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";

const patternQuestions = ref([
  {
    questionKey: "faq.questions.yarn_needle_size.question",
    answerKey: "faq.questions.yarn_needle_size.answer",
    isOpen: false,
  },
  {
    questionKey: "faq.questions.pattern_repetition.question",
    answerKey: "faq.questions.pattern_repetition.answer",
    isOpen: false,
  },
  {
    questionKey: "faq.questions.round_yoke_sweater.question",
    answerKey: "faq.questions.round_yoke_sweater.answer",
    isOpen: false,
  },
  {
    questionKey: "faq.questions.cardigan.question",
    answerKey: "faq.questions.cardigan.answer",
    isOpen: false,
  },
  {
    questionKey: "faq.questions.publish_patterns.question",
    answerKey: "faq.questions.publish_patterns.answer",
    isOpen: false,
  },
]);

const generalQuestions = ref([
  {
    questionKey: "faq.questions.what_is_knittable.question",
    answerKey: "faq.questions.what_is_knittable.answer",
    isOpen: false,
  },
  {
    questionKey: "faq.questions.english_parts.question",
    answerKey: "faq.questions.english_parts.answer",
    isOpen: false,
  },
  {
    questionKey: "faq.questions.change_language.question",
    answerKey: "faq.questions.change_language.answer",
    isOpen: false,
  },
]);

const togglePatternItem = (index) => {
  patternQuestions.value[index].isOpen = !patternQuestions.value[index].isOpen;
};

const toggleGeneralItem = (index) => {
  generalQuestions.value[index].isOpen = !generalQuestions.value[index].isOpen;
};
</script>

<style scoped lang="scss">
@import "../../scss/styles.scss";

.faq-page {
  max-width: 800px;
  margin: 0 auto;
  padding: 0 2rem;
}

.faq-title {
  @include heading1;
  color: var(--knittable-lav-darker);
  text-align: center;
  margin-bottom: 1rem;
}

.faq-subtitle {
  @include paragraphLargeRegular;
  color: var(--knittable-mocha);
  text-align: center;
  margin-bottom: 3rem;
}

.faq-container {
  background-color: white;
  border-radius: 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 2rem;
}

.faq-section {
  padding: 0;

  &:not(:last-child) {
    border-bottom: 2px solid var(--knittable-grey-light);
  }
}

.faq-section-title {
  @include subtitleSmall;
  color: var(--knittable-lav-darker);
  background-color: var(--knittable-lav-light);
  padding: 1rem 2rem;
  margin: 0;
  border-bottom: 1px solid var(--knittable-grey-light);
}

.faq-item {
  border-bottom: 1px solid var(--knittable-grey-light);

  &:last-child {
    border-bottom: none;
  }
}

.faq-question {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--knittable-lav-lighter);
  }
}

.question-text {
  @include subtitleSmall;
  color: var(--knittable-lav-darker);
  margin: 0;
  flex: 1;
  text-align: left;
}

.expand-icon {
  color: var(--knittable-lav-darker);
  transition: transform 0.2s ease;
}

.faq-answer {
  padding: 1rem 2rem 1.5rem;
  background-color: var(--knittable-isa);

  p {
    @include paragraphMediumRegular;
    color: var(--knittable-text);
    margin: 0;
    line-height: 1.6;
  }
}

.app-sm-and-down {
  .faq-page {
    padding: 0 1rem;
  }

  .faq-title {
    @include heading5;
  }

  .faq-section-title {
    padding: 0.75rem 1.25rem;
  }

  .faq-question {
    padding: 1rem 1.25rem;
  }

  .faq-answer {
    padding: 1rem 1.25rem;
  }
}
</style>
